<template>
  <button
    v-if="enabled"
    class="buy-button"
    :style="style"
    @click="onClick"
    @mouseover="hover = true"
    @mouseleave="hover = false"
  >
    <slot>Buy@Home</slot>
  </button>
</template>

<script>
import lodash from "lodash";
import api from "./api";
import "./showComponentOnLoad";

lodash.noConflict();

const CS_DISABLED_DEALER_IDS_KEY = "disable-cs-button-v2";

export default {
  name: "CsBuyWidget",
  props: {
    vin: {
      type: String,
      required: true,
    },
    dealerId: {
      type: String,
      required: true,
    },
    campaignId: {
      type: String,
      required: true,
    },
    provider: {
      type: String,
      required: false,
      default: "",
      validator: function (value) {
        return ["nissan", ""].indexOf(lodash.toUpper(value)) !== -1;
      },
    },
    source: {
      type: String,
      required: false,
      default: "dealer-plugin",
    },
    dev: {
      type: Boolean,
      required: false,
      default: false,
    },
    dealType: {
      type: String,
      required: false,
      default: "LOWEST",
      validator: function (value) {
        return (
          ["LEASE", "FINANCE", "LOWEST"].indexOf(lodash.toUpper(value)) !== -1
        );
      },
    },
    width: {
      type: String,
      required: false,
      default: null,
    },
    height: {
      type: String,
      required: false,
      default: null,
    },
    fontSize: {
      type: String,
      required: false,
      default: null,
    },
    font: {
      type: String,
      required: false,
      default: null,
    },
    fontColor: {
      type: String,
      required: false,
      default: "#fff",
    },
    textAlign: {
      type: String,
      required: false,
      default: "center",
      validator: function (value) {
        return (
          ["CENTER", "LEFT", "RIGHT"].indexOf(lodash.toUpper(value)) !== -1
        );
      },
    },
    padding: {
      type: String,
      required: false,
      default: "10px",
    },
    margin: {
      type: String,
      required: false,
      default: null,
    },
    backgroundColor: {
      type: String,
      required: false,
      default: "#c3002f",
    },
    hoverBackgroundColor: {
      type: String,
      required: false,
      default: "#9d0026",
    },
    test: {
      type: Boolean,
      required: false,
      default: false,
    },
    borderRadius: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      statusResponse: {
        active: false,
        dealerEnabled: true,
        domain: null,
        dealerSlug: "",
        dealerId: null,
        entryPoint: null,
      },
      hover: false,
    };
  },
  computed: {
    enabled() {
      if (this.test) {
        return true;
      }

      const active = lodash.get(this.statusResponse, "active", false);
      const domain = lodash.get(this.statusResponse, "domain", null);

      let isValidDomain = !lodash.isNil(domain);
      if (
        isValidDomain &&
        this.dev === false &&
        domain.indexOf("localhost") !== -1
      ) {
        // eslint-disable-next-line
        console.log("cs-buy-widget invalid domain:", domain);
        isValidDomain = false;
      }

      return active === true && isValidDomain;
    },
    style() {
      const style = {
        color: this.escapeBadCharacters(this.fontColor),
      };

      if (this.hover) {
        style.backgroundColor = this.escapeBadCharacters(
          this.hoverBackgroundColor
        );
      } else {
        style.backgroundColor = this.escapeBadCharacters(this.backgroundColor);
      }

      if (!lodash.isNil(this.width)) {
        style.width = this.escapeBadCharacters(this.width);
      }

      if (!lodash.isNil(this.height)) {
        style.height = this.escapeBadCharacters(this.height);
      }

      if (!lodash.isNil(this.fontSize)) {
        style.fontSize = this.escapeBadCharacters(this.fontSize);
      }

      if (!lodash.isNil(this.textAlign)) {
        style.textAlign = this.escapeBadCharacters(this.textAlign);
      }

      if (!lodash.isNil(this.padding)) {
        style.padding = this.escapeBadCharacters(this.padding);
      }

      if (!lodash.isNil(this.margin)) {
        style.margin = this.escapeBadCharacters(this.margin);
      }

      if (!lodash.isNil(this.font)) {
        style.font = this.escapeBadCharacters(this.font);
      } else {
        const fontSize = lodash.isNil(this.fontSize)
          ? "14px"
          : this.escapeBadCharacters(this.fontSize);

        style.font = `bold ${fontSize} Helvetica, Arial, sans-serif`;
      }

      if (!lodash.isNil(this.borderRadius)) {
        style.borderRadius = this.escapeBadCharacters(this.borderRadius);
      }

      return style;
    },
  },
  created() {
    this.onCreated();
  },
  mounted() {
    const that = this;
    window.setTimeout(() => that.$emit("onload"), 1);
  },
  methods: {
    onClick() {
      if (this.test) {
        alert("You are in test mode.");
        return false;
      }

      if (this.enabled) {
        const url = this.statusResponse.entryPoint;
        window.open(url, "_blank");
      }
    },
    onCreated() {
      if (this.dev === true) {
        // eslint-disable-next-line
        console.warn(
          "Running CarSaver Buy Button Widget in DEV mode.  Remove 'dev' attribute in production."
        );
      }

      if (this.test === true) {
        // eslint-disable-next-line
        console.warn(
          "Running CarSaver Buy Button Widget in TEST mode.  Remove 'test' attribute in production."
        );
      }

      const myStorage = window.sessionStorage;

      const storageDisabledDealerIds = JSON.parse(
        myStorage.getItem(CS_DISABLED_DEALER_IDS_KEY) || "[]"
      );

      if (lodash.indexOf(storageDisabledDealerIds, this.dealerId) !== -1) {
        this.logDebug("Disabling button due to session disablement.");

        this.statusResponse = {
          active: false,
          domain: null,
          dealerSlug: "",
          dealerId: null,
        };

        return;
      }

      if (lodash.isNil(this.vin) || this.vin.length != 17) {
        console.warn("Vin length invalid: ", this.vin);
        return;
      }

      const domain = location.hostname;

      const baseUri =
        this.dev === true
          ? "https://api-beta.carsaver.com"
          : "https://api.carsaver.com";

      const endpoint = baseUri + "/apex/dealerplugin/v1/vehicle/active";
      const query = {
        dealerId: this.escapeBadCharacters(this.dealerId),
        vin: this.vin,
        campaignId: this.escapeBadCharacters(this.campaignId),
        provider: this.escapeBadCharacters(this.provider),
        domain: encodeURIComponent(domain),
      };

      api
        .get(endpoint, query)
        .then((resp) => {
          this.statusResponse = lodash.get(resp, "data", {});

          if (!this.statusResponse.active) {
            const message =
              lodash.get(this.statusResponse, "message", "") || "";

            if (!this.statusResponse.dealerEnabled) {
              this.logDebug(`Disabling button for session due to: ${message}`);

              this.disableDealerId(this.dealerId);
            }
          }
        })
        .catch((error) => {
          // eslint-disable-next-line
          console.error("cs-buy-widget error:", error);
        });
    },

    disableDealerId(dealerId) {
      const myStorage = window.sessionStorage;
      const disabledIds = JSON.parse(
        myStorage.getItem(CS_DISABLED_DEALER_IDS_KEY) || "[]"
      );
      if (lodash.indexOf(disabledIds, dealerId) === -1) {
        disabledIds.push(dealerId);
        myStorage.setItem(
          CS_DISABLED_DEALER_IDS_KEY,
          JSON.stringify(disabledIds)
        );
      }
    },

    logDebug(message) {
      if (this.dev === true) {
        console.info(message);
      }
    },

    escapeBadCharacters(value) {
      if (lodash.isNil(value)) {
        return value;
      }
      return value.replace(/[\u201D]/g, "");
    },
  },
};
</script>

<style scoped lang="scss">
.buy-button {
  position: relative;
  border-radius: 0;
  color: white;
  border: none;
  cursor: pointer;

  &:not(:defined) {
    display: none;
  }

  svg {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    width: 1em;
    height: 1em;
    user-select: none;
    stroke-width: 0;
    stroke: currentColor;
    fill: currentColor;
  }
}
</style>
